import React, { useState, useEffect } from 'react';
import { Paper, Text, Tabs, Container } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import CharacterList from '../components/CharacterList';
import EffectsList from '../components/EffectsList';
import EffectProperties from '../components/EffectProperties';
import ItemsList from '../components/ItemsList';
import ItemProperties from '../components/ItemProperties';
import ActionsList from '../components/ActionsList';
import ActionProperties from '../components/ActionProperties';
import { CharacterProvider, useCharacterContext } from '../contexts/CharacterContext';
import { Effect } from '../api/effects';
import { Item } from '../api/items';
import { Action } from '../api/actions';
import { IconActivity, IconShield, IconGift, IconCommand } from '@tabler/icons-react';

/**
 * Dashboard Page
 * Main page of the application with responsive layout:
 * - Desktop: horizontal 3-panel layout (1/4, 1/2, 1/4)
 * - Mobile: vertical stack of panels with fixed height
 */
const DashboardContent: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { selectedCharacter } = useCharacterContext();
  
  // State for the selected effect
  const [selectedEffect, setSelectedEffect] = useState<Effect | null>(null);
  
  // State for the selected item
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  
  // State for the selected action
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  
  // State for tracking the active tab
  const [activeTab, setActiveTab] = useState<string | null>('effects');
  
  // State for server connection status
  const [serverConnected, setServerConnected] = useState(false);
  
  // Listen to server status events
  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const eventSource = new EventSource(`${apiUrl}/server/status/stream`, { withCredentials: true });
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setServerConnected(data.online);
        console.debug(`Server status update: ${data.online ? 'Online' : 'Offline'}`);
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };
    
    eventSource.onerror = () => {
      console.error('SSE connection error');
      setServerConnected(false);
    };
    
    return () => {
      eventSource.close();
    };
  }, []);
  
  // Reset selected items when tab changes and update active tab
  const handleTabChange = (value: string | null) => {
    setActiveTab(value);
    
    if (value !== 'effects') {
      setSelectedEffect(null);
    }
    if (value !== 'items') {
      setSelectedItem(null);
    }
    if (value !== 'actions') {
      setSelectedAction(null);
    }
  };
  
  // Render the properties panel based on active tab
  const renderPropertiesPanel = () => {
    if (activeTab === 'items') {
      return (
        <ItemProperties
          selectedItem={selectedItem}
          selectedCharacter={selectedCharacter}
          serverConnected={serverConnected}
        />
      );
    }
    
    if (activeTab === 'actions') {
      return (
        <ActionProperties
          selectedAction={selectedAction}
          selectedCharacter={selectedCharacter}
          serverConnected={serverConnected}
        />
      );
    }
    
    // Default to effects properties
    return (
      <EffectProperties 
        selectedEffect={selectedEffect}
        selectedCharacter={selectedCharacter}
        serverConnected={serverConnected}
      />
    );
  };
  
  return (
    <>
      {!isMobile ? (
        // Desktop Layout
        <div className="h-full w-full flex">
          {/* Left panel - Character List */}
          <div className="w-1/4 h-full">
            <CharacterList />
          </div>
          
          {/* Center panel - Items/Effects/Actions tabs */}
          <div className="w-2/4 h-full">
            <Paper shadow="xs" p="md" radius={0} h="100%" withBorder className="h-full flex flex-col overflow-hidden">
              <Tabs 
                value={activeTab}
                defaultValue="effects" 
                onChange={handleTabChange} 
                className="h-full flex flex-col"
                variant="pills"
                color="blue"
                radius="sm"
                classNames={{
                  tab: "font-medium transition-colors",
                  tabLabel: "font-medium"
                }}
              >
                <Tabs.List className="mb-3 px-1">
                  <Tabs.Tab 
                    value="effects" 
                    leftSection={<IconShield size={18} />}
                  >
                    Effects
                  </Tabs.Tab>
                  <Tabs.Tab 
                    value="items" 
                    leftSection={<IconGift size={18} />}
                  >
                    Items
                  </Tabs.Tab>
                  <Tabs.Tab 
                    value="actions" 
                    leftSection={<IconCommand size={18} />}
                  >
                    Actions
                  </Tabs.Tab>
                </Tabs.List>
                
                <Tabs.Panel value="effects" className="flex-grow overflow-hidden" px={0} pb={0}>
                  <EffectsList 
                    onEffectSelect={setSelectedEffect} 
                    selectedEffect={selectedEffect}
                  />
                </Tabs.Panel>
                
                <Tabs.Panel value="items" className="flex-grow overflow-hidden" px={0} pb={0}>
                  <ItemsList
                    onItemSelect={setSelectedItem}
                    selectedItem={selectedItem}
                  />
                </Tabs.Panel>
                
                <Tabs.Panel value="actions" className="flex-grow overflow-hidden" px={0} pb={0}>
                  <ActionsList
                    onActionSelect={setSelectedAction}
                    selectedAction={selectedAction}
                  />
                </Tabs.Panel>
              </Tabs>
            </Paper>
          </div>
          
          {/* Right panel - Properties */}
          <div className="w-1/4 h-full">
            {renderPropertiesPanel()}
          </div>
        </div>
      ) : (
        // Mobile Layout - vertical scrollable stack
        <div className="w-full">
          {/* Left panel - Character List */}
          <div className="w-full min-h-[200px] mb-2">
            <CharacterList />
          </div>
          
          {/* Center panel - Items/Effects/Actions tabs */}
          <div className="w-full min-h-[300px] mb-2">
            <Paper shadow="xs" p="md" radius={0} withBorder className="min-h-[300px] flex flex-col">
              <Tabs 
                value={activeTab}
                defaultValue="effects" 
                onChange={handleTabChange} 
                className="h-full flex flex-col"
                variant="pills"
                color="blue"
                radius="sm"
                classNames={{
                  tab: "font-medium transition-colors",
                  tabLabel: "font-medium"
                }}
              >
                <Tabs.List className="mb-3 px-1 border-b border-gray-200 pb-2">
                  <Tabs.Tab 
                    value="effects" 
                    leftSection={<IconShield size={18} />}
                  >
                    Effects
                  </Tabs.Tab>
                  <Tabs.Tab 
                    value="items" 
                    leftSection={<IconGift size={18} />}
                  >
                    Items
                  </Tabs.Tab>
                  <Tabs.Tab 
                    value="actions" 
                    leftSection={<IconCommand size={18} />}
                  >
                    Actions
                  </Tabs.Tab>
                </Tabs.List>
                
                <Tabs.Panel value="effects" className="flex-grow overflow-hidden" px={0} pb={0}>
                  <div className="h-[300px]">
                    <EffectsList 
                      onEffectSelect={setSelectedEffect} 
                      selectedEffect={selectedEffect}
                    />
                  </div>
                </Tabs.Panel>
                
                <Tabs.Panel value="items" className="flex-grow overflow-hidden" px={0} pb={0}>
                  <div className="h-[300px]">
                    <ItemsList
                      onItemSelect={setSelectedItem}
                      selectedItem={selectedItem}
                    />
                  </div>
                </Tabs.Panel>
                
                <Tabs.Panel value="actions" className="flex-grow overflow-hidden" px={0} pb={0}>
                  <div className="h-[300px]">
                    <ActionsList
                      onActionSelect={setSelectedAction}
                      selectedAction={selectedAction}
                    />
                  </div>
                </Tabs.Panel>
              </Tabs>
            </Paper>
          </div>
          
          {/* Right panel - Properties */}
          <div className="w-full min-h-[200px]">
            {renderPropertiesPanel()}
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Dashboard Page with Character Provider
 */
const DashboardPage: React.FC = () => {
  return (
    <CharacterProvider>
      <DashboardContent />
    </CharacterProvider>
  );
};

export default DashboardPage; 